// ============================================================
//  src/pages/CartPage.jsx
//  Updated: Material Icons Round + DM Sans / Playfair Display
//  (fonts & icons loaded globally via App.jsx useGlobalAssets)
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Input, Button, Toast } from "../components/UI";
import ProductImage from "../components/ProductImage";
import { calcCartTotals, changeCartQty, sendOrderToWhatsApp } from "../utils/helpers";
import { supabase } from "../utils/supabase";
import AddressPage from "./AddressPage";
import config from "../config";

// ── Material Icon helper ──────────────────────────────────────
function Icon({ name, style = {} }) {
  return (
    <span
      className="material-icons-round"
      style={{ fontSize: 20, verticalAlign: "middle", lineHeight: 1, ...style }}
    >
      {name}
    </span>
  );
}

// ── Step progress bar ─────────────────────────────────────────
function StepBar({ current }) {
  const steps = [
    { id: "cart",    label: "Cart",    icon: "shopping_cart" },
    { id: "address", label: "Address", icon: "local_shipping" },
    { id: "payment", label: "Payment", icon: "account_balance" },
  ];
  return (
    <div className="step-bar">
      {steps.map((s, i) => {
        const done   = steps.findIndex((x) => x.id === current) > i;
        const active = current === s.id;
        return (
          <div key={s.id} className={`step-item ${active ? "active" : ""} ${done ? "done" : ""}`}>
            <div className="step-label" style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "'DM Sans', sans-serif" }}>
              <Icon name={s.icon} style={{ fontSize: 15 }} />
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Cart step ─────────────────────────────────────────────────
function CartStep({ cart, setCart, onNext, user }) {
  const navigate = useNavigate();
  const { subtotal } = calcCartTotals(cart);

  if (!cart || cart.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 20 }}>
        <Icon name="shopping_cart" style={{ fontSize: 56, color: config.colors.primary }} />
        <div style={{ fontWeight: 800, fontSize: 18, color: config.colors.primary, fontFamily: "'Playfair Display', serif" }}>
          Your cart is empty
        </div>
        <div style={{ color: "#777", fontFamily: "'DM Sans', sans-serif" }}>
          Add some products to proceed to checkout.
        </div>
      </div>
    );
  }

  return (
    <>
      <Card className="cart-card">
        {cart.map((item) => (
          <div key={item.id} className="cart-item">
            <div className="cart-item-thumb">
              <ProductImage src={item.image} emoji={item.emoji} alt={item.name} color={item.color} height={64} fontSize={26} />
            </div>
            <div className="cart-item-body">
              <div className="cart-item-title" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.name}</div>
              <div className="cart-item-meta"  style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.selectedSize || item.weight}</div>
              <div className="cart-item-price" style={{ fontFamily: "'DM Sans', sans-serif" }}>₹{item.price * item.qty}</div>
            </div>
            <div className="cart-item-qty">
              <QtyBtn onClick={() => setCart((c) => changeCartQty(c, item.id, -1))}>−</QtyBtn>
              <span className="qty-count">{item.qty}</span>
              <QtyBtn onClick={() => setCart((c) => changeCartQty(c, item.id, +1))}>+</QtyBtn>
            </div>
          </div>
        ))}
        <div className="cart-total-row">
          <Row label="Total" value={`₹${subtotal}`} bold />
        </div>
      </Card>

      <Button
        className="proceed-btn"
        fullWidth
        onClick={() => { if (!user) { navigate("/signin"); } else { onNext(); } }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'DM Sans', sans-serif" }}>
          {user ? (
            <><Icon name="arrow_forward" style={{ fontSize: 18 }} /> Proceed to Address</>
          ) : (
            <><Icon name="lock" style={{ fontSize: 18 }} /> Sign In to Continue</>
          )}
        </span>
      </Button>
    </>
  );
}

// ── Bank detail row ───────────────────────────────────────────
function BankRow({ label, value, copyable = false }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "10px 0", borderBottom: `1px solid ${config.colors.cardBorder}`, gap: 10,
    }}>
      <div>
        <div style={{
          fontSize: 10, color: "#bbb", fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 14, fontWeight: 700, color: config.colors.text,
          fontFamily: copyable ? "'Courier New', monospace" : "'DM Sans', sans-serif",
          letterSpacing: copyable ? 0.5 : 0,
        }}>
          {value}
        </div>
      </div>
      {copyable && (
        <button
          onClick={handleCopy}
          className="btn-press"
          style={{
            flexShrink: 0,
            background: copied ? "#e8f5e9" : "#f5f5f5",
            border: `1px solid ${copied ? "#a5d6a7" : "#e0e0e0"}`,
            borderRadius: 8, padding: "5px 10px", fontSize: 11,
            fontWeight: 700, color: copied ? config.colors.primary : "#888",
            cursor: "pointer", transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 4,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <Icon name={copied ? "check" : "content_copy"} style={{ fontSize: 13, color: copied ? config.colors.primary : "#888" }} />
          {copied ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  );
}

// ── Payment step ──────────────────────────────────────────────
function PaymentStep({ cart, grandTotal, onPlace, onBack, placing }) {
  const { bank } = config;
  return (
    <>
      {/* Bank transfer hero */}
      <div style={{
        background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`,
        borderRadius: 18, padding: "22px 20px", color: "#fff",
        textAlign: "center", marginBottom: 14,
      }}>
        <div style={{
          fontSize: 11, opacity: 0.75, letterSpacing: 1,
          textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif",
        }}>
          Pay via Bank Transfer
        </div>
        <Icon name="account_balance" style={{ fontSize: 52, color: "#fff", margin: "10px 0 6px", display: "block" }} />
        <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.9, fontFamily: "'DM Sans', sans-serif" }}>{bank.bankName}</div>
        <div style={{ opacity: 0.75, fontSize: 12, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>{bank.branch}</div>
        <div style={{
          background: "rgba(255,255,255,0.15)", borderRadius: 12,
          padding: "10px 16px", display: "inline-block", marginTop: 14,
        }}>
          <div style={{ fontSize: 11, opacity: 0.8, fontFamily: "'DM Sans', sans-serif" }}>Amount to Transfer</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900 }}>₹{grandTotal}</div>
        </div>
      </div>

      {/* Bank details */}
      <Card>
        <div style={{
          fontWeight: 800, fontSize: 15, marginBottom: 4,
          display: "flex", alignItems: "center", gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <Icon name="account_balance" style={{ fontSize: 18, color: config.colors.primary }} />
          Bank Account Details
        </div>
        <div style={{ fontSize: 12, color: "#aaa", marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
          Use these details to transfer the payment
        </div>
        <BankRow label="Account Name"   value={bank.accountName} />
        <BankRow label="Account Number" value={bank.accountNumber} copyable />
        <BankRow label="IFSC Code"      value={bank.ifscCode}      copyable />
        <BankRow label="Bank Name"      value={bank.bankName} />
        <BankRow label="Branch"         value={bank.branch} />
        <BankRow label="Account Type"   value={bank.accountType} />
      </Card>

      {/* Order summary */}
      <Card>
        <div style={{
          fontWeight: 800, marginBottom: 10,
          display: "flex", alignItems: "center", gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <Icon name="receipt_long" style={{ fontSize: 18, color: config.colors.primary }} />
          Order Summary
        </div>
        {cart.map((i) => (
          <div key={i.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            justifyContent: "space-between", fontSize: 13, color: "#666", marginBottom: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, overflow: "hidden",
                flexShrink: 0, border: `1px solid ${config.colors.cardBorder}`,
              }}>
                <ProductImage src={i.image} emoji={i.emoji} alt={i.name} color={i.color} height={36} fontSize={16} />
              </div>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>
                {i.name} ({i.selectedSize || "-"}) × {i.qty}
              </span>
            </div>
            <span style={{ fontWeight: 700, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>₹{i.price * i.qty}</span>
          </div>
        ))}
        <div style={{
          borderTop: `1px solid ${config.colors.cardBorder}`, marginTop: 10, paddingTop: 10,
          display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <span>Grand Total</span>
          <span style={{ color: config.colors.primary }}>₹{grandTotal}</span>
        </div>
      </Card>

      {/* Instructions */}
      <div style={{
        background: "#fff3e0", border: "1px solid #ffd180",
        borderRadius: 12, padding: 14, fontSize: 13, color: "#e65100",
        marginBottom: 14, lineHeight: 1.9, fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ fontWeight: 800, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="info" style={{ fontSize: 16, color: "#e65100" }} />
          Note
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            `Transfer ₹${grandTotal} to the bank account above`,
            "Use NEFT / IMPS / online banking",
            `Tap "Confirm Order" — bank details will be sent to our WhatsApp`,
            "Share your payment screenshot on WhatsApp to confirm",
            "Once confirmed, we'll process your order and notify you on WhatsApp!",
            "Free delivery in 3–5 business days on orders worth ₹1,000 or more.",
          ].map((tip, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Icon name="chevron_right" style={{ fontSize: 15, color: "#e65100", marginTop: 1, flexShrink: 0 }} />
              <span>{tip}</span>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="whatsapp"
        fullWidth
        style={{ marginBottom: 10, fontSize: 15, padding: "15px 0", opacity: placing ? 0.7 : 1 }}
        onClick={() => onPlace()}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'DM Sans', sans-serif" }}>
          {placing ? (
            <><Icon name="hourglass_top" style={{ fontSize: 18 }} /> Placing Order...</>
          ) : (
            <><Icon name="check_circle" style={{ fontSize: 18 }} /> Confirm Order &amp; Send to WhatsApp</>
          )}
        </span>
      </Button>

      <Button variant="outline" fullWidth onClick={onBack}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif" }}>
          <Icon name="arrow_back" style={{ fontSize: 16 }} />
          Back to Address
        </span>
      </Button>
    </>
  );
}

// ── Main CartPage ─────────────────────────────────────────────
export default function CartPage({ cart, setCart, auth, onPlacingChange }) {
  const navigate = useNavigate();
  const user = auth?.user ?? null;

  const [step, setStep]                       = useState("cart");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [toast, setToast]                     = useState(null);
  const [placing, setPlacing]                 = useState(false);
  const navTimer = useRef(null);

  useEffect(() => {
    return () => { if (navTimer.current) clearTimeout(navTimer.current); };
  }, []);

  function setPlacingWithNotify(val) {
    setPlacing(val);
    if (onPlacingChange) onPlacingChange(val);
  }

  const totals = calcCartTotals(cart);

  async function placeOrder(snapCart, snapAddress) {
    if (!user)   { navigate("/signin"); return; }
    if (placing) return;
    setPlacingWithNotify(true);
    if (navTimer.current) clearTimeout(navTimer.current);

    if (!snapCart || snapCart.length === 0) {
      setToast("Cart is empty. Please add items first.");
      setPlacingWithNotify(false);
      return;
    }

    const snapTotals = calcCartTotals(snapCart);

    try {
      const orderItems = snapCart.map((item) => ({
        id: item.id, name: item.name, price: item.price, qty: item.qty,
        size: item.selectedSize || item.weight || "", subtotal: item.price * item.qty,
      }));

      const { error } = await supabase.from("orders_table").insert({
        user_id:    user.id,
        items:      orderItems,
        total:      snapTotals.subtotal,
        created_at: new Date().toISOString(),
        address: {
          line1:   snapAddress.line1,
          line2:   snapAddress.line2,
          city:    snapAddress.city,
          state:   snapAddress.state,
          pincode: snapAddress.pincode,
        },
      }).select();

      if (error) {
        console.error("Order insert error:", error);
        setToast("Order could not be saved. Please try again.");
        return;
      }

      sendOrderToWhatsApp({ user, address: snapAddress, cart: snapCart });
      setToast("Order placed & sent to WhatsApp!");

      navTimer.current = setTimeout(() => {
        navTimer.current = null;
        setStep("cart");
        setSelectedAddress(null);
        setCart([]);
        navigate("/orders");
      }, 2600);

    } catch (err) {
      console.error("placeOrder failed:", err);
      setToast("Something went wrong. Please try again.");
    } finally {
      setPlacingWithNotify(false);
    }
  }

  // Empty cart full-page state
  if (cart.length === 0 && step === "cart") {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "60vh", padding: 24, textAlign: "center",
      }}>
        <Icon name="shopping_cart" style={{ fontSize: 64, color: config.colors.primary }} />
        <div style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 22, fontWeight: 800, color: config.colors.primary, marginTop: 14,
        }}>
          Cart is empty
        </div>
        <div style={{ color: "#aaa", margin: "6px 0 22px", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
          Add some organic goodness!
        </div>
        <Button onClick={() => navigate("/")}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif" }}>
            <Icon name="arrow_back" style={{ fontSize: 16 }} />
            Shop Now
          </span>
        </Button>
        {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      </div>
    );
  }

  return (
    <div className="cart-page-container">
      <StepBar current={step} />

      {step === "cart" && (
        <CartStep cart={cart} setCart={setCart} user={user} onNext={() => setStep("address")} />
      )}
      {step === "address" && (
        <AddressPage
          auth={auth}
          onProceed={(addr) => { setSelectedAddress(addr); setStep("payment"); }}
          onBack={() => setStep("cart")}
        />
      )}
      {step === "payment" && (
        <PaymentStep
          cart={cart}
          address={selectedAddress}
          grandTotal={totals.subtotal}
          deliveryTotal={totals.deliveryTotal}
          onPlace={() => placeOrder(cart, selectedAddress)}
          onBack={() => setStep("address")}
          placing={placing}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

// ── Qty button ────────────────────────────────────────────────
function QtyBtn({ children, onClick }) {
  return (
    <button
      className="btn-press"
      onClick={onClick}
      style={{
        background: "#f0f7ee", border: "none", borderRadius: 8,
        width: 32, height: 32, fontWeight: 800, fontSize: 18,
        cursor: "pointer", color: config.colors.primary,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {children}
    </button>
  );
}

// ── Summary row ───────────────────────────────────────────────
function Row({ label, value, muted, bold }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      fontSize: bold ? 17 : 14, fontWeight: bold ? 800 : 400,
      color: muted ? "#999" : config.colors.text,
      marginBottom: bold ? 0 : 5,
      borderTop: bold ? `2px dashed ${config.colors.cardBorder}` : "none",
      paddingTop: bold ? 10 : 0, marginTop: bold ? 4 : 0,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <span>{label}</span>
      <span style={bold ? { color: config.colors.primary } : {}}>{value}</span>
    </div>
  );
}
